import {
  ActionIcon,
  Card,
  Group,
  Select,
  Stack,
  TextInput,
  Textarea
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import {
  IconBellRinging,
  IconCircleFilled,
  IconTrash
} from '@tabler/icons-react'
import { useIntl } from 'react-intl'
import { AlertSeverity } from '@/types/vlm'
import { slugify } from '@/utils/slugify'
import { PipelineFormValues } from './VlmPipelineForm'

const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  low: 'var(--mantine-color-blue-6)',
  medium: 'var(--mantine-color-yellow-6)',
  high: 'var(--mantine-color-orange-6)',
  critical: 'var(--mantine-color-red-6)'
}

type AlertConditionCardProps = {
  form: UseFormReturnType<PipelineFormValues>
  index: number
}

export const AlertConditionCard = ({
  form,
  index
}: AlertConditionCardProps) => {
  const intl = useIntl()
  const severity = form.values.alertPrompts[index].severity

  const severityOptions = [
    {
      value: 'low',
      label: intl.formatMessage({
        id: 'vlm.pipelines.create.alertConditions.severity.low'
      })
    },
    {
      value: 'medium',
      label: intl.formatMessage({
        id: 'vlm.pipelines.create.alertConditions.severity.medium'
      })
    },
    {
      value: 'high',
      label: intl.formatMessage({
        id: 'vlm.pipelines.create.alertConditions.severity.high'
      })
    },
    {
      value: 'critical',
      label: intl.formatMessage({
        id: 'vlm.pipelines.create.alertConditions.severity.critical'
      })
    }
  ]

  return (
    <Card padding="md" pos="relative" withBorder>
      <ActionIcon
        color="red"
        variant="subtle"
        pos="absolute"
        top={8}
        right={8}
        onClick={() => form.removeListItem('alertPrompts', index)}
      >
        <IconTrash size={16} />
      </ActionIcon>

      <Stack gap="xs" pt={4}>
        <Group gap="xs" align="flex-start" wrap="nowrap">
          <TextInput
            size="xs"
            style={{ flex: 1 }}
            label={intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.name.label'
            })}
            placeholder={intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.name.placeholder'
            })}
            leftSection={
              <IconBellRinging
                size={16}
                color="var(--mantine-color-orange-6)"
              />
            }
            {...form.getInputProps(`alertPrompts.${index}.name`)}
            onChange={(event) => {
              const value = event.currentTarget.value
              form.setFieldValue(`alertPrompts.${index}.name`, value)
              // alert_tag is generated automatically from name and is used on
              // the next step to attach VLM actions to specific alert tags
              form.setFieldValue(
                `alertPrompts.${index}.alert_tag`,
                slugify(value)
              )
            }}
          />
          <Select
            size="xs"
            w={200}
            label={intl.formatMessage({
              id: 'vlm.pipelines.create.alertConditions.severity.label'
            })}
            data={severityOptions}
            allowDeselect={false}
            leftSection={
              <IconCircleFilled size={12} color={SEVERITY_COLORS[severity]} />
            }
            {...form.getInputProps(`alertPrompts.${index}.severity`)}
          />
        </Group>
        <Textarea
          size="xs"
          label={intl.formatMessage({
            id: 'vlm.pipelines.create.alertConditions.description.label'
          })}
          placeholder={intl.formatMessage({
            id: 'vlm.pipelines.create.alertConditions.description.placeholder'
          })}
          minRows={3}
          maxRows={6}
          autosize
          {...form.getInputProps(`alertPrompts.${index}.description`)}
        />
      </Stack>
    </Card>
  )
}
